import React, { useState, useEffect, useRef } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  IconButton, 
  Select, 
  MenuItem, 
  InputLabel, 
  FormControl, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Paper,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { 
  AddCircleOutline, 
  Person, 
  Delete as DeleteIcon,
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  ManageSearch as ManageSearchIcon,
  FilterList as FilterListIcon,
  Group as GroupIcon,
  PersonOutline as PersonOutlineIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://inkedupmanager.com/api';

const ClientsPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [clients, setClients] = useState([]);
  const [allClients, setAllClients] = useState([]); // Store all clients from API
  const [sortBy, setSortBy] = useState('first_name');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // NEW: View toggle state
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'mine'
  const [isStudioOwner, setIsStudioOwner] = useState(false);
  const [studioInfo, setStudioInfo] = useState(null);
  
  // Bulk download states
  const [bulkDownloadDialogOpen, setBulkDownloadDialogOpen] = useState(false);
  const [bulkDownloadLoading, setBulkDownloadLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState('');
  const [downloadResults, setDownloadResults] = useState(null);
  
  // Date range filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterError, setFilterError] = useState(null);
  
  const navigate = useNavigate();
  
  // NEW: Function to check if user is studio owner
  const checkStudioOwnership = async (token) => {
    try {
      const studioResponse = await fetch(`${API_BASE_URL}/studio/info/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (studioResponse.ok) {
        const studioData = await studioResponse.json();
        if (studioData.studio && studioData.is_owner) {
          setIsStudioOwner(true);
          setStudioInfo(studioData);
          return true;
        }
      }
    } catch (error) {
      console.error('Error checking studio ownership:', error);
    }
    return false;
  };

  // FIXED: Separate fetchClients function that can be called independently
  const fetchClients = async (currentViewMode = viewMode) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      // Check studio ownership first
      const isOwner = await checkStudioOwnership(token);
      // FIXED: Build URL with proper view parameter
      let url = `${API_BASE_URL}/clients/`;
      if (isOwner && currentViewMode === 'mine') {
        url += '?view=mine_only';
      }
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle both old and new response formats
      let clientsArray = [];
      
      if (Array.isArray(data)) {
        // Old format: direct array
        clientsArray = data;
      } else if (data && data.clients && Array.isArray(data.clients)) {
        // New format: {clients: [...], total: X}
        clientsArray = data.clients;
      } else {
        console.error("Unexpected clients response format:", data);
        clientsArray = [];
      }
      setAllClients(clientsArray);
      setClients(clientsArray);
      setError(null);
      
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients');
      
      if (error.message && error.message.includes('401')) {
        navigate('/signin');
      }
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Initial load - only run once
  useEffect(() => {
    let isMounted = true;
    let timer = null;
    
    const initialLoad = async () => {
      if (isMounted) {
        await fetchClients();
      }
    };
    
    initialLoad();
    
    timer = setTimeout(() => {
      if (isMounted && loading) {
        setLoading(false);
      }
    }, 5000);
    
    return () => {
      isMounted = false;
      if (timer) clearTimeout(timer);
    };
  }, [navigate, getAccessTokenSilently]); // FIXED: Removed viewMode from dependencies

  // FIXED: Handle view mode change with immediate refetch
  const handleViewModeChange = async (event, newViewMode) => {
    if (newViewMode !== null && isStudioOwner) {
      setViewMode(newViewMode);
      setLoading(true);
      await fetchClients(newViewMode); // Pass the new view mode directly
    }
  };

  // NEW: Function to filter clients based on view mode
  const filterClientsByView = (clientsData, mode) => {
    if (!isStudioOwner || mode === 'all') {
      return clientsData; // Show all clients (API already filters appropriately)
    }
    
    // For 'mine' mode, we need to filter to only the current user's clients
    // We can identify the owner's clients by checking if they don't have a different owner
    // This is a bit tricky since the API doesn't return ownership info
    // For now, we'll implement this on the backend and use a query parameter
    return clientsData;
  };

  const handleSortChange = (event) => {
    const field = event.target.value;
    setSortBy(field);
    const sortedClients = [...clients].sort((a, b) =>
      a[field].localeCompare(b[field])
    );
    setClients(sortedClients);
  };

  const handleDeleteClient = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete || !clientToDelete.id) {
      console.error('No client selected for deletion or missing ID');
      setDeleteDialogOpen(false);
      return;
    }
    
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      const response = await fetch(`${API_BASE_URL}/clients/${clientToDelete.id}/delete/`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setClients(clients.filter(c => c.id !== clientToDelete.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting client:', error);
      setError('Failed to delete client');
    }
  };

  // Open bulk download dialog
  const handleBulkDownloadClick = () => {
    setBulkDownloadDialogOpen(true);
    setFilterError(null);
    setDownloadResults(null);
  };

  // Validate date range
  const validateDateRange = () => {
    if (startDate && endDate && startDate > endDate) {
      setFilterError('Start date must be before end date.');
      return false;
    }
    setFilterError(null);
    return true;
  };

  // Handle date changes with validation
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setFilterError(null);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setFilterError(null);
  };

  // ENHANCED: Studio-aware bulk download with comprehensive permission handling
  const downloadAllConsentFormsAsZip = async () => {
    if (!validateDateRange()) {
      return;
    }

    setBulkDownloadLoading(true);
    setDownloadProgress(0);
    setDownloadStatus('Collecting consent forms and custom forms...');
    setDownloadResults(null);

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        },
      });

      setDownloadProgress(25);
      setDownloadStatus('Gathering form IDs from all accessible clients...');

      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Use the current client list (which already respects view mode and studio permissions)
      const clientsToProcess = clients;
      // Collect all consent form IDs and custom form IDs from accessible clients
      const allConsentFormIds = [];
      const allCustomFormIds = [];
      const processingErrors = [];

      for (let i = 0; i < clientsToProcess.length; i++) {
        const client = clientsToProcess[i];
        
        try {
          // Fetch consent forms for this client
          const consentFormsResponse = await fetch(`${API_BASE_URL}/clients/${client.id}/consent-forms/`, {
            headers
          });

          if (consentFormsResponse.ok) {
            const consentForms = await consentFormsResponse.json();
            
            if (consentForms && consentForms.length > 0) {
              // Filter consent forms by date range if specified
              const filteredConsentForms = consentForms.filter(form => {
                if (!startDate && !endDate) return true; // No date filter
                
                const formDate = form.date_signed ? new Date(form.date_signed).toISOString().split('T')[0] : null;
                if (!formDate) return false; // Skip forms without date
                
                if (startDate && formDate < startDate) return false;
                if (endDate && formDate > endDate) return false;
                
                return true;
              });

              // Add consent form IDs
              filteredConsentForms.forEach(form => {
                allConsentFormIds.push(form.id);
              });
            }
          } else if (consentFormsResponse.status === 403) {
            // Permission issue - this is expected for some clients in studio mode
          } else {
            processingErrors.push(`Failed to fetch consent forms for client ${client.first_name} ${client.last_name} (${consentFormsResponse.status})`);
          }

          // Fetch custom form submissions for this client
          const customFormsResponse = await fetch(`${API_BASE_URL}/custom-forms/submissions/?client_id=${client.id}`, {
            headers
          });

          if (customFormsResponse.ok) {
            const customForms = await customFormsResponse.json();
            
            if (customForms && customForms.length > 0) {
              // Filter custom forms by date range if specified
              const filteredCustomForms = customForms.filter(form => {
                if (!startDate && !endDate) return true; // No date filter
                
                const formDate = form.date_signed ? new Date(form.date_signed).toISOString().split('T')[0] : null;
                if (!formDate) return false; // Skip forms without date
                
                if (startDate && formDate < startDate) return false;
                if (endDate && formDate > endDate) return false;
                
                return true;
              });

              // Add custom form IDs
              filteredCustomForms.forEach(form => {
                allCustomFormIds.push(form.id);
              });
            }
          } else if (customFormsResponse.status === 403) {
            // Permission issue - this is expected for some clients in studio mode
          } else {
            processingErrors.push(`Failed to fetch custom forms for client ${client.first_name} ${client.last_name} (${customFormsResponse.status})`);
          }
        } catch (clientError) {
          console.error(`Error fetching forms for client ${client.id}:`, clientError);
          processingErrors.push(`Error processing client ${client.first_name} ${client.last_name}: ${clientError.message}`);
        }
      }

      const totalForms = allConsentFormIds.length + allCustomFormIds.length;

      if (totalForms === 0) {
        const errorMsg = viewMode === 'all' 
          ? 'No consent forms or custom forms found from accessible clients for the specified criteria. You may have access to view clients but not their forms, or the clients may not have any forms yet.'
          : 'No consent forms or custom forms found for the specified criteria.';
        throw new Error(errorMsg);
      }

      setDownloadProgress(50);
      setDownloadStatus(`Preparing ${totalForms} forms for download (${allConsentFormIds.length} consent, ${allCustomFormIds.length} custom)...`);

      // Call the enhanced studio-aware bulk download API endpoint
      const bulkDownloadResponse = await fetch(`${API_BASE_URL}/consent-forms/bulk-download/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          consent_form_ids: allConsentFormIds,    // Array of consent form IDs
          custom_form_ids: allCustomFormIds,      // Array of custom form IDs  
          reason: `Studio-aware bulk download from clients page (${viewMode} mode) with comprehensive PDFs including images and all form data`
        })
      });

      if (!bulkDownloadResponse.ok) {
        const errorData = await bulkDownloadResponse.json().catch(() => ({}));
        
        // Handle specific error cases
        if (bulkDownloadResponse.status === 400) {
          throw new Error(`Invalid request: ${errorData.error || 'Bad request'}`);
        } else if (bulkDownloadResponse.status === 403) {
          throw new Error(`Access denied: ${errorData.error || 'You do not have permission to download these forms'}`);
        } else if (bulkDownloadResponse.status === 500) {
          throw new Error(`Server error: ${errorData.error || 'Internal server error occurred during download'}`);
        } else {
          throw new Error(errorData.error || `HTTP error! status: ${bulkDownloadResponse.status}`);
        }
      }

      setDownloadProgress(75);
      setDownloadStatus('Packaging files...');

      // Download the ZIP file
      const blob = await bulkDownloadResponse.blob();
      
      // Validate that we received a proper ZIP file
      if (!blob || blob.size === 0) {
        throw new Error('Received empty file from server');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const dateRangeStr = startDate && endDate ? `_${startDate}_to_${endDate}` : '';
      const viewModeStr = viewMode === 'all' ? '_studio_all' : '_mine_only';
      link.setAttribute('download', `forms_comprehensive${viewModeStr}${dateRangeStr}_${timestamp}.zip`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloadStatus('Download complete!');
      setDownloadProgress(100);

      // Enhanced success results with proper messaging about scope
      const scopeMessage = viewMode === 'all' && isStudioOwner 
        ? ` (Includes forms from all studio clients you have access to)`
        : '';
      
      const successMessage = `Successfully downloaded ${totalForms} comprehensive forms as ZIP file (${allConsentFormIds.length} consent forms, ${allCustomFormIds.length} custom forms with all questions, answers, and images)${scopeMessage}.`;
      const warningsMessage = processingErrors.length > 0 ? ` Note: ${processingErrors.length} client(s) had processing warnings.` : '';

      setDownloadResults({
        successful: totalForms,
        failed: processingErrors.length,
        errors: processingErrors,
        message: successMessage + warningsMessage
      });

      // Close dialog after successful download
      setTimeout(() => {
        setBulkDownloadDialogOpen(false);
        setStartDate('');
        setEndDate('');
        setDownloadStatus('');
        setDownloadProgress(0);
      }, 3000);
      
    } catch (error) {
      console.error('Error downloading forms:', error);
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to download forms. ';
      
      if (error.message.includes('No consent forms or custom forms found')) {
        errorMessage = error.message; // Use the full error message
      } else if (error.message.includes('Network')) {
        errorMessage += 'Network connection issue. Please check your internet connection and try again.';
      } else if (error.message.includes('Server error')) {
        errorMessage += 'Server encountered an error. Please try again in a few minutes.';
      } else if (error.message.includes('Access denied')) {
        errorMessage += 'You do not have permission to download these forms. Please check with your administrator.';
      } else {
        errorMessage += error.message || 'An unexpected error occurred.';
      }
      
      setError(errorMessage);
      setDownloadResults({
        successful: 0,
        failed: 1,
        errors: [error.message || 'Download failed'],
        message: errorMessage
      });
      setDownloadStatus('');
      setDownloadProgress(0);
    } finally {
      setBulkDownloadLoading(false);
    }
  };

  // Show loading indicator
  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography>Loading clients...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Page Header with Add Client Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#222' }}>
            {viewMode === 'mine' ? 'My Clients' : 'Your Clients'}
          </Typography>
          {isStudioOwner && studioInfo && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              {studioInfo.studio.studio_name} • {clients.length} client{clients.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Manage All Forms Button */}
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ManageSearchIcon />}
            onClick={() => navigate('/bulk-consent-forms')}
          >
            Manage All Forms
          </Button>

          {/* Bulk Download Button */}
          {clients.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={handleBulkDownloadClick}
            >
              Download All Forms (Consent & Custom)
            </Button>
          )}
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={() => navigate('/add-client')}
          >
            Add New Client
          </Button>
        </Box>
      </Box>

      {/* NEW: Studio Owner View Toggle */}
      {isStudioOwner && (
        <Box sx={{ mb: 3 }}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Client View:
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="client view mode"
                size="small"
              >
                <ToggleButton value="all" aria-label="all clients">
                  <GroupIcon sx={{ mr: 1 }} />
                  All Clients
                </ToggleButton>
                <ToggleButton value="mine" aria-label="my clients only">
                  <PersonOutlineIcon sx={{ mr: 1 }} />
                  My Clients Only
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                {viewMode === 'all' 
                  ? 'Showing your clients and your invited team members\' clients' 
                  : 'Showing only clients you personally added'
                }
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Show error if there is one */}
      {error && (
        <Box sx={{ mb: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Download Results */}
      {downloadResults && (
        <Box sx={{ mb: 4 }}>
          <Alert 
            severity={downloadResults.failed === 0 ? "success" : downloadResults.successful === 0 ? "error" : "warning"}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              {downloadResults.message}
              {downloadResults.successful > 0 && (
                <Chip 
                  label={`${downloadResults.successful} successful`} 
                  color="success" 
                  size="small" 
                  sx={{ ml: 1 }} 
                />
              )}
              {downloadResults.failed > 0 && (
                <Chip 
                  label={`${downloadResults.failed} failed`} 
                  color="error" 
                  size="small" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Typography>
            
            {downloadResults.errors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="error">
                  Errors:
                </Typography>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {downloadResults.errors.slice(0, 5).map((error, index) => (
                    <li key={index}>
                      <Typography variant="caption" color="error">
                        {error}
                      </Typography>
                    </li>
                  ))}
                </ul>
                {downloadResults.errors.length > 5 && (
                  <Typography variant="caption" color="error">
                    ... and {downloadResults.errors.length - 5} more errors
                  </Typography>
                )}
              </Box>
            )}
          </Alert>
          
          <Button 
            size="small" 
            onClick={() => setDownloadResults(null)}
            variant="outlined"
          >
            Dismiss
          </Button>
        </Box>
      )}

      {/* Only show sorting when there are clients */}
      {clients.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="sort-by-label">Sort By</InputLabel>
            <Select
              labelId="sort-by-label"
              value={sortBy}
              onChange={handleSortChange}
              label="Sort By"
            >
              <MenuItem value="first_name">First Name</MenuItem>
              <MenuItem value="last_name">Last Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Clients List */}
      <Grid container spacing={3}>
        {clients.length === 0 ? (
          <Grid item xs={12}>
            <Typography align="center">
              {viewMode === 'mine' 
                ? 'No personal clients found. Use the "Add New Client" button to create one.'
                : 'No clients found. Use the "Add New Client" button to create one.'
              }
            </Typography>
          </Grid>
        ) : (
          clients.map((client) => (
            <Grid item xs={12} sm={6} md={4} key={client.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person sx={{ fontSize: 40, color: '#007BFF' }} />
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="h6">
                        {client.first_name} {client.last_name}
                      </Typography>
                      <Typography color="textSecondary">{client.email}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => navigate(`/client/${client.id}`)}
                    >
                      View Profile
                    </Button>
                    <IconButton aria-label="delete" onClick={() => handleDeleteClient(client)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Bulk Download Dialog */}
      <Dialog 
        open={bulkDownloadDialogOpen} 
        onClose={() => !bulkDownloadLoading && setBulkDownloadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FileDownloadIcon sx={{ mr: 1 }} />
            Download All Forms
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Download all consent forms and custom forms from your accessible clients as a comprehensive ZIP file with complete PDFs including all questions, answers, and uploaded images. 
            {isStudioOwner && (
              <><br/><br/>
              <strong>Studio Access:</strong> As a studio owner, you can download forms from all studio clients you have permission to access. The system will automatically include only forms you're authorized to view.
              </>
            )}
            You can optionally filter by date range.
          </DialogContentText>

          {/* Date Range Filters */}
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Date Range Filter (Optional)
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0]
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0],
                    min: startDate || undefined
                  }}
                />
              </Grid>
            </Grid>

            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Leave empty to download all forms regardless of date.
            </Typography>
          </Paper>

          {/* Download Description */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            <strong>What's included:</strong> Complete PDF documents with all form questions, answers, digital signatures, uploaded images, and compliance information for both consent forms and custom forms{isStudioOwner && viewMode === 'all' ? ' from all accessible studio clients' : ''}.
          </Typography>

          {/* Filter Error */}
          {filterError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {filterError}
            </Alert>
          )}

          {/* Download Progress */}
          {bulkDownloadLoading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                {downloadStatus}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={downloadProgress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {downloadProgress}% complete
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBulkDownloadDialogOpen(false)}
            disabled={bulkDownloadLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={downloadAllConsentFormsAsZip}
            variant="contained"
            color="primary"
            startIcon={bulkDownloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
            disabled={bulkDownloadLoading}
          >
            {bulkDownloadLoading ? 'Downloading...' : 'Download Comprehensive ZIP'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this client? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientsPage;